import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Feather } from "lucide-react";

const LAST_UPDATED = "January 2026";

const Layout = ({ title, kicker, children }) => (
  <div className="min-h-screen bg-[#F4F1EA] text-[#1C1A17]">
    <header className="border-b border-[#DCD7C9]">
      <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="legal-back"
          className="inline-flex items-center gap-2 text-sm text-[#0A1E3F] hover:text-[#D4A53A] transition-colors"
        >
          <ArrowLeft size={14} /> Back to The Story Assembly
        </Link>
        <div className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
            alt="The Story Assembly"
            className="h-7 w-7 rounded-md object-cover"
          />
          <span className="font-serif text-base tracking-tight hidden sm:inline">
            The <span className="italic font-light">Story</span>{" "}
            <span className="text-[#D4A53A]">Assembly</span>
          </span>
        </div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
        {kicker} · Last updated {LAST_UPDATED}
      </div>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-3 text-[#0A1E3F]">
        {title}
      </h1>
      <div className="mt-10 space-y-6 text-[16px] leading-relaxed text-[#1C1A17]/85">
        {children}
      </div>
      <div className="mt-16 pt-8 border-t border-[#DCD7C9] flex items-center gap-2 text-sm">
        <Feather size={14} className="text-[#D4A53A]" />
        <span className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
          Questions? Email{" "}
          <a
            href="mailto:hello@storyassembly.in"
            className="text-[#0A1E3F] hover:text-[#D4A53A]"
          >
            hello@storyassembly.in
          </a>
        </span>
      </div>
    </main>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#0A1E3F] mt-8">
    {children}
  </h2>
);
const P = ({ children }) => <p>{children}</p>;
const UL = ({ children }) => (
  <ul className="list-disc pl-5 space-y-2">{children}</ul>
);

export const PrivacyPolicy = () => (
  <Layout title="Privacy Policy" kicker="Legal">
    <P>
      The Story Assembly ("we", "us") respects your privacy. This policy explains
      what we collect, why we collect it, and how we use it.
    </P>

    <H2>1. Information we collect</H2>
    <UL>
      <li>
        <strong>Brand inputs</strong> you submit to the generator (brand name,
        idea, audience, tone preference) — used solely to generate your output.
      </li>
      <li>
        <strong>Contact form data</strong> (name, email, message) — used to
        respond to your enquiry.
      </li>
      <li>
        <strong>Payment confirmation details</strong> (name, email, UPI
        Reference ID) — used to verify your payment and activate access.
      </li>
      <li>
        <strong>Basic usage logs</strong> (IP, timestamp, user agent) for
        security and abuse-prevention.
      </li>
    </UL>

    <H2>2. How we use your data</H2>
    <UL>
      <li>To deliver the service you requested (story / tagline generation).</li>
      <li>
        To send transactional emails (payment receipts, founder notifications)
        via Resend.
      </li>
      <li>To respond to your questions or feedback.</li>
      <li>To improve the product (in aggregate, never linked to you).</li>
    </UL>

    <H2>3. What we DO NOT do</H2>
    <UL>
      <li>We do not sell your data.</li>
      <li>We do not use your brand inputs to train any AI model.</li>
      <li>We do not share your information with third parties for marketing.</li>
    </UL>

    <H2>4. Third-party services</H2>
    <P>
      We use the following sub-processors strictly to operate the product:
      MongoDB (storage), Resend (email delivery), Anthropic / OpenAI / Google
      (AI text generation via the Emergent integrations layer), and
      Emergent's hosting platform.
    </P>

    <H2>5. Your rights</H2>
    <P>
      You can request a copy of your data or request deletion at any time by
      emailing{" "}
      <a
        href="mailto:hello@storyassembly.in"
        className="underline text-[#0A1E3F]"
      >
        hello@storyassembly.in
      </a>
      . We respond within 7 working days.
    </P>

    <H2>6. Changes</H2>
    <P>
      We may update this policy occasionally. The "Last updated" date at the
      top reflects the most recent revision.
    </P>
  </Layout>
);

export const TermsOfService = () => (
  <Layout title="Terms of Service" kicker="Legal">
    <P>
      By using The Story Assembly you agree to the following terms. If you do not
      agree, please do not use the service.
    </P>

    <H2>1. The service</H2>
    <P>
      The Story Assembly is an AI-assisted brand-storytelling tool. The Free plan
      lets you generate up to 3 outputs per day. Paid plans unlock unlimited
      generations and additional features.
    </P>

    <H2>2. Acceptable use</H2>
    <UL>
      <li>Do not use the service to generate content that is illegal, hateful, or designed to deceive.</li>
      <li>Do not attempt to reverse-engineer or scrape the generator.</li>
      <li>You are responsible for the brand inputs you provide.</li>
    </UL>

    <H2>3. Ownership of output</H2>
    <P>
      You own the brand stories, taglines, tone of voice and elevator pitches
      generated for your inputs. We retain no exclusive rights over your output.
    </P>

    <H2>4. Paid plans &amp; payments</H2>
    <P>
      Pro is ₹499 per month. The 1:1 Brand Strategy Session is a
      one-time fee of ₹4,999 (introductory offer). Payments are accepted via
      UPI to the merchant ID displayed at checkout. Access is activated within
      a few working hours after we verify your UPI Reference ID.
    </P>

    <H2>5. Refund policy</H2>
    <P>
      See our separate{" "}
      <Link to="/refund" className="underline text-[#0A1E3F]">
        Refund Policy
      </Link>
      .
    </P>

    <H2>6. Disclaimer</H2>
    <P>
      AI output is generated probabilistically and should always be reviewed
      before use in production marketing. We do not guarantee any specific
      business outcome.
    </P>

    <H2>7. Limitation of liability</H2>
    <P>
      To the maximum extent permitted by law, our total liability is limited to
      the amount you paid us in the 12 months preceding the claim.
    </P>

    <H2>8. Contact</H2>
    <P>
      Questions? Write to{" "}
      <a
        href="mailto:hello@storyassembly.in"
        className="underline text-[#0A1E3F]"
      >
        hello@storyassembly.in
      </a>
      .
    </P>
  </Layout>
);

export const RefundPolicy = () => (
  <Layout title="Refund Policy" kicker="Legal">
    <P>
      We've kept this policy deliberately simple. Please read it carefully{" "}
      <strong>before</strong> you pay — payments are non-refundable once digital
      assets have been delivered.
    </P>

    <H2>All paid plans · No refund</H2>
    <UL>
      <li>
        Pro (₹499) and Brand Strategy Session (₹4,999) are{" "}
        <strong>non-refundable</strong> once payment is confirmed and access
        (Pro magic link, Brand Kit PDF, or session booking) has been delivered.
      </li>
      <li>
        We deliver digital assets — brand stories, taglines, voice guidance and
        the 4-page Brand Kit PDF — that cannot be returned or "un-used" once
        downloaded. The free demo (3 generations/day) exists so you can test the
        quality of the output before paying.
      </li>
      <li>
        Pro is a monthly subscription — you can cancel anytime to stop future
        charges, but the current billing period is non-refundable.
      </li>
    </UL>

    <H2>Brand Strategy Session reschedules</H2>
    <P>
      We do not refund the ₹4,999 session, but we'll happily{" "}
      <strong>reschedule once</strong> if you reach out at least 24 hours before
      your slot. No-shows are forfeited.
    </P>

    <H2>Failed payments</H2>
    <P>
      If a UPI payment was debited but we couldn't activate your access (e.g. we
      didn't receive the funds, or the verification failed), email{" "}
      <a
        href="mailto:hello@storyassembly.in"
        className="underline text-[#0A1E3F]"
      >
        hello@storyassembly.in
      </a>{" "}
      with your UPI Reference ID. We'll either complete activation or refund the
      failed payment back to the source UPI account within 5–7 working days.
    </P>

    <H2>Before you pay</H2>
    <P>
      Try the free demo, read the FAQ, and reach out at{" "}
      <a
        href="mailto:hello@storyassembly.in"
        className="underline text-[#0A1E3F]"
      >
        hello@storyassembly.in
      </a>{" "}
      if you have any questions. We'd much rather answer your question before
      you pay than have you regret a purchase afterwards.
    </P>
  </Layout>
);

export default PrivacyPolicy;
